import {
  type NotificationBase,
  type NotificationFromQuery,
} from '@/store/notifications/notifications'

import { type Props as PropsNotificationLeaveRequestCreated } from '@/components/PmNotification/PmNotificationLeaveRequestCreated/PmNotificationLeaveRequestCreated.vue'
import { type Props as PropsNotificationLeaveRequestUpdated } from '@/components/PmNotification/PmNotificationLeaveRequestUpdated/PmNotificationLeaveRequestUpdated.vue'
import { type Props as PropsNotificationLeaveRequestAccepted } from '@/components/PmNotification/PmNotificationLeaveRequestAccepted/PmNotificationLeaveRequestAccepted.vue'
import { type Props as PropsNotificationLeaveRequestDeclined } from '@/components/PmNotification/PmNotificationLeaveRequestDeclined/PmNotificationLeaveRequestDeclined.vue'
import { type Props as PropsNotificationLeaveRequestCancelRequested } from '@/components/PmNotification/PmNotificationLeaveRequestCancelRequested/PmNotificationLeaveRequestCancelRequested.vue'
import { type Props as PropsNotificationLeaveRequestCancelDeclined } from '@/components/PmNotification/PmNotificationLeaveRequestCancelDeclined/PmNotificationLeaveRequestCancelDeclined.vue'
import { type Props as PropsNotificationLeaveRequestCancelAccepted } from '@/components/PmNotification/PmNotificationLeaveRequestCancelAccepted/PmNotificationLeaveRequestCancelAccepted.vue'
import { type Props as PropsNotificationLeaveRequestAborted } from '@/components/PmNotification/PmNotificationLeaveRequestAborted/PmNotificationLeaveRequestAborted.vue'
import { type Props as PropsNotificationLeaveRequestAcceptedNotice } from '@/components/PmNotification/PmNotificationLeaveRequestAcceptedNotice/PmNotificationLeaveRequestAcceptedNotice.vue'
import { type Props as PropsNotificationLeaveRequestPartiallyAccepted } from '@/components/PmNotification/PmNotificationLeaveRequestPartiallyAccepted/PmNotificationLeaveRequestPartiallyAccepted.vue'
import { type Props as PropsNotificationLeaveRequestDeclinedNotice } from '@/components/PmNotification/PmNotificationLeaveRequestDeclinedNotice/PmNotificationLeaveRequestDeclinedNotice.vue'
import { type Props as PropsNotificationLeaveRequestCancelAcceptedNotice } from '@/components/PmNotification/PmNotificationLeaveRequestCancelAcceptedNotice/PmNotificationLeaveRequestCancelAcceptedNotice.vue'
import { type Props as PropsNotificationLeaveRequestCancelPartiallyAccepted } from '@/components/PmNotification/PmNotificationLeaveRequestCancelPartiallyAccepted/PmNotificationLeaveRequestCancelPartiallyAccepted.vue'
import { type Props as PropsNotificationLeaveRequestCancelDeclinedNotice } from '@/components/PmNotification/PmNotificationLeaveRequestCancelDeclinedNotice/PmNotificationLeaveRequestCancelDeclinedNotice.vue'
import { parseServerDateString } from '@/utilities/date'
import { getDisplayNameOfAddress } from '@/utilities/string'

export interface NotificationLeaveRequestCreated {
  type: 'NotificationLeaveRequestCreated'
  props: Omit<
    PropsNotificationLeaveRequestCreated,
    keyof NotificationBase | 'isJumpVisible'
  >
}

export function normalizeNotificationLeaveRequestCreated(
  notification: NotificationFromQuery
) {
  if (notification.meta?.__typename !== 'LeaveRequestCreated') {
    throw new Error('notification.meta.__typename is not LeaveRequestCreated')
  }

  const details = getLeaveRequestDetails(notification)

  const result: NotificationLeaveRequestCreated = {
    type: 'NotificationLeaveRequestCreated',
    props: {
      leaveRequestId: details.id,
      name: getDisplayNameOfAddress(notification.meta.user),
      startDate: parseServerDateString(notification.meta.startDate, {
        type: 'date',
      }),
      endDate: parseServerDateString(notification.meta.endDate, {
        type: 'date',
      }),
      type: notification.meta.type,
    },
  }

  return result
}

export interface NotificationLeaveRequestAborted {
  type: 'NotificationLeaveRequestAborted'
  props: Omit<
    PropsNotificationLeaveRequestAborted,
    keyof NotificationBase | 'isJumpVisible'
  >
}

export function normalizeNotificationLeaveRequestAborted(
  notification: NotificationFromQuery
) {
  const shared = normalizeSharedLeaveRequestDetails(notification)

  const result: NotificationLeaveRequestAborted = {
    type: 'NotificationLeaveRequestAborted',
    props: shared,
  }

  return result
}

export interface NotificationLeaveRequestUpdated {
  type: 'NotificationLeaveRequestUpdated'
  props: Omit<
    PropsNotificationLeaveRequestUpdated,
    keyof NotificationBase | 'isJumpVisible'
  >
}

export function normalizeNotificationLeaveRequestUpdated(
  notification: NotificationFromQuery
) {
  const details = getLeaveRequestDetails(notification)
  const shared = normalizeSharedLeaveRequestDetails(notification)

  const result: NotificationLeaveRequestUpdated = {
    type: 'NotificationLeaveRequestUpdated',
    props: {
      ...shared,
      leaveRequestOfUserId: details.user.id,
    },
  }

  return result
}

export interface NotificationLeaveRequestAccepted {
  type: 'NotificationLeaveRequestAccepted'
  props: Omit<PropsNotificationLeaveRequestAccepted, keyof NotificationBase>
}

export function normalizeNotificationLeaveRequestAccepted(
  notification: NotificationFromQuery
) {
  const shared = normalizeSharedLeaveRequestDetails(notification)

  const result: NotificationLeaveRequestAccepted = {
    type: 'NotificationLeaveRequestAccepted',
    props: {
      leaveRequestId: shared.leaveRequestId,
      startDate: shared.startDate,
      endDate: shared.endDate,
      type: shared.type,
    },
  }

  return result
}

export interface NotificationLeaveRequestAcceptedNotice {
  type: 'NotificationLeaveRequestAcceptedNotice'
  props: Omit<
    PropsNotificationLeaveRequestAcceptedNotice,
    keyof NotificationBase
  >
}

export function normalizeNotificationLeaveRequestAcceptedNotice(
  notification: NotificationFromQuery
) {
  const shared = normalizeSharedLeaveRequestDetails(notification)

  const result: NotificationLeaveRequestAcceptedNotice = {
    type: 'NotificationLeaveRequestAcceptedNotice',
    props: shared,
  }

  return result
}

export interface NotificationLeaveRequestPartiallyAccepted {
  type: 'NotificationLeaveRequestPartiallyAccepted'
  props: Omit<
    PropsNotificationLeaveRequestPartiallyAccepted,
    keyof NotificationBase
  >
}

export function normalizeNotificationLeaveRequestPartiallyAccepted(
  notification: NotificationFromQuery
) {
  const shared = normalizeSharedLeaveRequestDetails(notification)

  const result: NotificationLeaveRequestPartiallyAccepted = {
    type: 'NotificationLeaveRequestPartiallyAccepted',
    props: shared,
  }

  return result
}

export interface NotificationLeaveRequestDeclined {
  type: 'NotificationLeaveRequestDeclined'
  props: Omit<PropsNotificationLeaveRequestDeclined, keyof NotificationBase>
}

export function normalizeNotificationLeaveRequestDeclined(
  notification: NotificationFromQuery
) {
  const shared = normalizeSharedLeaveRequestDetails(notification)

  const result: NotificationLeaveRequestDeclined = {
    type: 'NotificationLeaveRequestDeclined',
    props: {
      leaveRequestId: shared.leaveRequestId,
      startDate: shared.startDate,
      endDate: shared.endDate,
      type: shared.type,
    },
  }

  return result
}

export interface NotificationLeaveRequestDeclinedNotice {
  type: 'NotificationLeaveRequestDeclinedNotice'
  props: Omit<
    PropsNotificationLeaveRequestDeclinedNotice,
    keyof NotificationBase
  >
}

export function normalizeNotificationLeaveRequestDeclinedNotice(
  notification: NotificationFromQuery
) {
  const shared = normalizeSharedLeaveRequestDetails(notification)

  const result: NotificationLeaveRequestDeclinedNotice = {
    type: 'NotificationLeaveRequestDeclinedNotice',
    props: shared,
  }

  return result
}

export interface NotificationLeaveRequestCancelRequested {
  type: 'NotificationLeaveRequestCancelRequested'
  props: Omit<
    PropsNotificationLeaveRequestCancelRequested,
    keyof NotificationBase
  >
}

export function normalizeNotificationLeaveRequestCancelRequested(
  notification: NotificationFromQuery
) {
  const shared = normalizeSharedLeaveRequestDetails(notification)

  const result: NotificationLeaveRequestCancelRequested = {
    type: 'NotificationLeaveRequestCancelRequested',
    props: shared,
  }

  return result
}

export interface NotificationLeaveRequestCancelDeclined {
  type: 'NotificationLeaveRequestCancelDeclined'
  props: Omit<
    PropsNotificationLeaveRequestCancelDeclined,
    keyof NotificationBase
  >
}

export function normalizeNotificationLeaveRequestCancelDeclined(
  notification: NotificationFromQuery
) {
  const shared = normalizeSharedLeaveRequestDetails(notification)

  const result: NotificationLeaveRequestCancelDeclined = {
    type: 'NotificationLeaveRequestCancelDeclined',
    props: shared,
  }

  return result
}

export interface NotificationLeaveRequestCancelDeclinedNotice {
  type: 'NotificationLeaveRequestCancelDeclinedNotice'
  props: Omit<
    PropsNotificationLeaveRequestCancelDeclinedNotice,
    keyof NotificationBase
  >
}

export function normalizeNotificationLeaveRequestCancelDeclinedNotice(
  notification: NotificationFromQuery
) {
  const shared = normalizeSharedLeaveRequestDetails(notification)

  const result: NotificationLeaveRequestCancelDeclinedNotice = {
    type: 'NotificationLeaveRequestCancelDeclinedNotice',
    props: shared,
  }

  return result
}

export interface NotificationLeaveRequestCancelAccepted {
  type: 'NotificationLeaveRequestCancelAccepted'
  props: Omit<
    PropsNotificationLeaveRequestCancelAccepted,
    keyof NotificationBase
  >
}

export function normalizeNotificationLeaveRequestCancelAccepted(
  notification: NotificationFromQuery
) {
  const shared = normalizeSharedLeaveRequestDetails(notification)

  const result: NotificationLeaveRequestCancelAccepted = {
    type: 'NotificationLeaveRequestCancelAccepted',
    props: shared,
  }

  return result
}

export interface NotificationLeaveRequestCancelAcceptedNotice {
  type: 'NotificationLeaveRequestCancelAcceptedNotice'
  props: Omit<
    PropsNotificationLeaveRequestCancelAcceptedNotice,
    keyof NotificationBase
  >
}

export function normalizeNotificationLeaveRequestCancelAcceptedNotice(
  notification: NotificationFromQuery
) {
  const shared = normalizeSharedLeaveRequestDetails(notification)

  const result: NotificationLeaveRequestCancelAcceptedNotice = {
    type: 'NotificationLeaveRequestCancelAcceptedNotice',
    props: shared,
  }

  return result
}

export interface NotificationLeaveRequestCancelPartiallyAccepted {
  type: 'NotificationLeaveRequestCancelPartiallyAccepted'
  props: Omit<
    PropsNotificationLeaveRequestCancelPartiallyAccepted,
    keyof NotificationBase
  >
}

export function normalizeNotificationLeaveRequestCancelPartiallyAccepted(
  notification: NotificationFromQuery
) {
  const shared = normalizeSharedLeaveRequestDetails(notification)

  const result: NotificationLeaveRequestCancelPartiallyAccepted = {
    type: 'NotificationLeaveRequestCancelPartiallyAccepted',
    props: shared,
  }

  return result
}

function normalizeSharedLeaveRequestDetails(
  notification: NotificationFromQuery
) {
  const details = getLeaveRequestDetails(notification)

  return {
    leaveRequestId: details.id,
    name: getDisplayNameOfAddress(details.user),
    startDate: parseServerDateString(details.startDate),
    endDate: parseServerDateString(details.endDate),
    type: details.type,
  }
}

// TODO: Should this be an assertion instead?
function getLeaveRequestDetails(notification: NotificationFromQuery) {
  if (!notification.details) {
    throw new Error('notification.details is missing')
  }

  if (notification.details.__typename !== 'LeaveRequest') {
    throw new Error('notification.details.__typename is not LeaveRequest')
  }

  return notification.details
}
